import { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import Home from "./pages/Home";
import Create from "./pages/Create";
import FairLaunchPage from "./pages/FairLaunch";
import FairLaunchCreate from "./pages/FairLaunchCreate";
import FairLaunchMarket from "./pages/FairLaunchMarket";

import NotFound from "./pages/NotFound"; // eager — used in routing tests and must render synchronously

// Secondary and optional pages are lazy-loaded on demand.
import {
  About, Risk, Terms, Privacy, AcceptableUse, CreatorRewards,
  Networks, MyLaunches, Recent, TokenProfile, LaunchStudio,
  AdminTeamAccess,
  WalletHome, WalletConnect, Portfolio, TransactionCentre,
  Send, Receive, SecurityCentre, NotificationCentre,
  LegalIndex, UnauditedBeta, NonAffiliation, SmartContractRisk,
  ContentModeration, ContractAddresses, CreatorRecipientImmutability,
  IncidentResponse, IrreversibleTransactions, NoGuaranteedReturns,
  PartnersCCC, MembersCCC, AdminCCC,
} from "./App.lazy";

/** Minimal fallback while a lazy chunk loads. */
function PageLoader() {
  return (
    <div
      className="flex min-h-[60vh] items-center justify-center"
      role="status"
      aria-label="Loading page"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent opacity-40" />
    </div>
  );
}

export default function App() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Core (eagerly loaded) ───────────────────────────────── */}
          <Route path="/" element={<Home />} />
          <Route path="/launch-studio" element={<LaunchStudio />} />

          {/* ── Network roadmap ─────────────────────────────────────── */}
          <Route path="/networks" element={<Networks />} />
          <Route path="/roadmap" element={<Navigate to="/networks" replace />} />
          <Route path="/chains" element={<Navigate to="/networks" replace />} />

          {/* ── Chain-aware launch routes ────────────────────────────── */}
          <Route path="/launch" element={<Navigate to="/launch/robinhood" replace />} />
          <Route path="/launch/robinhood" element={<Create />} />
          <Route path="/launch/robinhood/standard" element={<Create />} />
          <Route path="/launch/robinhood/fair" element={<FairLaunchCreate />} />
          <Route path="/launch/:chain" element={<Networks />} />

          {/* ── Chain-aware discovery routes ─────────────────────────── */}
          <Route path="/discover" element={<Recent />} />
          <Route path="/discover/robinhood" element={<Recent />} />
          <Route path="/discover/:chain" element={<Networks />} />

          {/* ── Legacy / existing routes ─────────────────────────────── */}
          <Route path="/create" element={<Create />} />
          <Route path="/my-launches" element={<MyLaunches />} />
          <Route path="/my-trades" element={<MyLaunches />} />
          <Route path="/recent" element={<Recent />} />
          <Route path="/about" element={<About />} />
          <Route path="/risk" element={<Risk />} />

          {/* ── Token profiles ───────────────────────────────────────── */}
          <Route path="/token/:chainId/:contractAddress" element={<TokenProfile />} />

          {/* ── Fair Launch ──────────────────────────────────────────── */}
          <Route path="/fair-launch" element={<FairLaunchPage />} />
          <Route path="/fair-launch/create" element={<FairLaunchCreate />} />
          <Route path="/fair-launch/market/:chainId/:tokenAddress" element={<FairLaunchMarket />} />

          {/* ── Markets alias ────────────────────────────────────────── */}
          <Route path="/markets" element={<FairLaunchPage />} />

          {/* ── Legal ────────────────────────────────────────────────── */}
          <Route path="/legal" element={<LegalIndex />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/creator-rewards" element={<CreatorRewards />} />
          <Route path="/legal/unaudited-beta" element={<UnauditedBeta />} />
          <Route path="/legal/non-affiliation" element={<NonAffiliation />} />
          <Route path="/legal/smart-contract-risk" element={<SmartContractRisk />} />
          <Route path="/legal/content-moderation" element={<ContentModeration />} />
          <Route path="/legal/contract-addresses" element={<ContractAddresses />} />
          <Route path="/legal/creator-recipient-immutability" element={<CreatorRecipientImmutability />} />
          <Route path="/legal/incident-response" element={<IncidentResponse />} />
          <Route path="/legal/irreversible-transactions" element={<IrreversibleTransactions />} />
          <Route path="/legal/no-guaranteed-returns" element={<NoGuaranteedReturns />} />

          {/* ── CCC Members Hub ──────────────────────────────────────── */}
          <Route path="/partners/ccc" element={<PartnersCCC />} />
          <Route path="/members/ccc"  element={<MembersCCC />}  />
          <Route path="/admin/ccc"    element={<AdminCCC />}    />

          {/* ── Admin ────────────────────────────────────────────────── */}
          <Route path="/admin/team-access" element={<AdminTeamAccess />} />

          {/* ── Wallet (feature-flagged; disabled in production) ─────── */}
          <Route path="/wallet" element={<WalletHome />} />
          <Route path="/wallet/connect" element={<WalletConnect />} />
          <Route path="/wallet/portfolio" element={<Portfolio />} />
          <Route path="/wallet/transactions" element={<TransactionCentre />} />
          <Route path="/wallet/send" element={<Send />} />
          <Route path="/wallet/receive" element={<Receive />} />
          <Route path="/wallet/security" element={<SecurityCentre />} />
          <Route path="/wallet/notifications" element={<NotificationCentre />} />
          <Route path="/wallet/bridge" element={<Navigate to="/fair-launch" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}
